import { useState, useMemo, useCallback } from 'react';
import { Controller, useFormContext, useFieldArray } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import { Input } from '@components/Input';
import Autocomplete from '@components/Autocomplete';
import { DeleteIcon } from 'assets/images/icons';
import { useTranslation } from '@hooks';
import { FORM_TYPE } from '@constants/brands';

const LocationWrapper = styled('div')({
    display: 'grid',
    rowGap: '8px',
    gridTemplateColumns: 'calc(100% - 40px)',
    '@media (max-width: 600px)': {
        gridTemplateColumns: '90%',
        gridGap: '16px',
    },
});

const StyledIconButton = styled(IconButton)({
    position: 'absolute',
    right: '-40px',
    top: '5px',
    padding: '4px',
    '& svg path': {
        fill: 'rgba(0, 0, 0, 0.6)',
    },
});

const Settings = ({ locations, modal, selectedBrand }) => {
    const [searchMain, setSearchMain] = useState('');
    const [searchLocation, setSearchLocation] = useState({});

    const { control, setValue, isSubmitting, watch } = useFormContext();
    const { t } = useTranslation();

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'locations',
    });

    const selectedMainLocation = watch('primaryLocation');
    const watchLocations = watch('locations');
    const selectedLocations = fields.map((_, index) => {
        return { ...watchLocations[index], id: watchLocations[index]['id'][0]?.id };
    });

    const availableLocations = useMemo(() => {
        const filteredLocations = locations.filter(l =>
            modal.type === FORM_TYPE.NEW ? !l.brand : !l.brand || l.brand?.id === selectedBrand?.id,
        );
        return filteredLocations.filter(
            l => +selectedMainLocation[0]?.id !== l.companyId && !selectedLocations.some(sl => +sl.id === l.companyId),
        );
    }, [locations, modal.type, selectedMainLocation, selectedLocations]);

    const mainOptionsWithSearch = useMemo(() => {
        const filteredBySearch = availableLocations.filter(l =>
            l.internalName.toLowerCase().includes(searchMain.toLowerCase()),
        );
        const filteredBySelected =
            modal.type === FORM_TYPE.NEW
                ? filteredBySearch
                : filteredBySearch.filter(l => !selectedLocations.some(sl => +sl.id === l.companyId));

        return filteredBySelected.map(l => ({ id: `${l.companyId}`, label: l.internalName }));
    }, [availableLocations, modal.type, searchMain, selectedLocations]);

    const otherOptionsWithSearch = useCallback(
        fieldIndex => {
            const filteredLocations = searchLocation[fieldIndex]
                ? availableLocations.filter(l =>
                      l.internalName.toLowerCase().includes(searchLocation[fieldIndex]?.toLowerCase()),
                  )
                : availableLocations;
            const filteredBySelectedMain =
                modal.type === FORM_TYPE.NEW
                    ? filteredLocations
                    : filteredLocations.filter(
                          l =>
                              +selectedMainLocation[0]?.id !== l.companyId &&
                              !selectedLocations.some(sl => +sl.id === l.companyId),
                      );
            return filteredBySelectedMain.map(l => ({ id: `${l.companyId}`, label: l.internalName }));
        },
        [availableLocations, modal.type, searchLocation, selectedMainLocation, selectedLocations],
    );

    return (
        <>
            <Controller
                control={control}
                name="title"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('common.title')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                    />
                )}
            />
            <LocationWrapper>
                <div style={{ position: 'relative' }}>
                    <Controller
                        control={control}
                        name="primaryLocation"
                        render={({ field: { ref, ...field }, fieldState: { error } }) => (
                            <Autocomplete
                                {...field}
                                inputRef={ref}
                                label={t('SettingsBrands.mainLocation')}
                                options={mainOptionsWithSearch}
                                value={field.value}
                                isOptionEqualToValue={(option, value) => option.id === +value.id}
                                disabled={isSubmitting}
                                inputProps={{
                                    value: searchMain,
                                    onChange: e => setSearchMain(e.target.value),
                                    style: { display: field?.value?.length > 0 ? 'none' : 'block' },
                                }}
                                onChangeAutoComplete={items => {
                                    setValue('primaryLocation', items.length ? items : [], {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                    setSearchMain('');
                                }}
                                required
                                error={error}
                            />
                        )}
                    />
                    {availableLocations.length > 0 && (
                        <StyledIconButton
                            sx={{ right: '-42px', top: '3px' }}
                            onClick={() => append({ id: '', isMainLocation: false })}
                        >
                            <AddIcon />
                        </StyledIconButton>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexFlow: 'column-reverse' }}>
                    {fields.map((formField, index) => (
                        <div style={{ position: 'relative' }} key={formField.id}>
                            <Controller
                                control={control}
                                name={`locations[${index}].id`}
                                render={({ field: { ref, ...field } }) => (
                                    <Autocomplete
                                        {...field}
                                        inputRef={ref}
                                        label={t('common.location')}
                                        options={otherOptionsWithSearch(formField.id)}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        disabled={isSubmitting}
                                        inputProps={{
                                            value: searchLocation[formField.id] ?? '',
                                            onChange: e =>
                                                setSearchLocation(prev => ({
                                                    ...prev,
                                                    [formField.id]: e.target.value,
                                                })),
                                            style: { display: field?.value?.length > 0 ? 'none' : 'block' },
                                        }}
                                        onChangeAutoComplete={items => {
                                            setValue(`locations[${index}].id`, items.length ? items : [], {
                                                shouldDirty: true,
                                            });
                                            setSearchLocation(prev => ({ ...prev, [formField.id]: '' }));
                                        }}
                                    />
                                )}
                            />
                            <StyledIconButton onClick={() => remove(index)}>
                                <DeleteIcon />
                            </StyledIconButton>
                        </div>
                    ))}
                </div>
            </LocationWrapper>
        </>
    );
};

export default Settings;
